import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { PartnerSubscription } from '@/api/partners/subscriptions/types';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import {
  capitalizeFirstLetter,
  formatCurrency,
  formatInOriginalOffset,
} from '@/core/utils';
import { Text } from '@/ui';
import DataTable from '@/ui/data-table';

import {
  IdLink,
  PlateVinCell,
  SubscriptionStatusChip,
} from './reservation-parts';
import { getRenewalPeriodLabel } from './status-label';

type SubscriptionTableProps = {
  /** Subscriptions for the current page only. */
  subscriptions: PartnerSubscription[];
  page: number;
  total: number | undefined;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
};

export const SubscriptionTable = ({
  subscriptions,
  page,
  total,
  rowsPerPage,
  onPageChange,
}: SubscriptionTableProps) => {
  const { styles } = useStyles(stylesheet);
  const { isEVFMC } = useHasOrganization();

  // EVFMC partners get two extra columns: billing cadence and outstanding balance.
  const columns = [
    {
      key: 'sub-id',
      title: 'SUBSCRIPTION ID',
      style: { minWidth: 150 },
    },
    { key: 'customer', title: 'CUSTOMER', style: { minWidth: 160 } },
    { key: 'status', title: 'STATUS', style: { minWidth: 190 } },
    { key: 'start-time', title: 'START TIME', style: { minWidth: 190 } },
    {
      key: 'renewal-period',
      title: 'RENEWAL PERIOD',
      style: { minWidth: 140 },
    },
    ...(isEVFMC
      ? [
          {
            key: 'billing-cadence',
            title: 'BILLING CADENCE',
            style: { minWidth: 140 },
          },
        ]
      : []),
    { key: 'car-details', title: 'PLATE | VIN', style: { minWidth: 180 } },
    {
      key: 'latest-trip-id',
      title: 'LATEST TRIP ID',
      style: { minWidth: 140 },
    },
    ...(isEVFMC
      ? [
          {
            key: 'outstanding-balance',
            title: 'OUTSTANDING BALANCE',
            style: { minWidth: 180 },
          },
        ]
      : []),
  ];

  const rows = subscriptions.map((subscription) => ({
    key: subscription.id,
    id: subscription.id,
    'sub-id': (
      <IdLink
        id={subscription.id}
        href={`/partner/reservations/subscriptions/${subscription.id}`}
      />
    ),
    customer: (
      <Text type="caption1" style={styles.secondaryText}>
        {`${subscription.customer.firstName} ${subscription.customer.lastName}`}
      </Text>
    ),
    status: <SubscriptionStatusChip status={subscription.status} />,
    'start-time': (
      <Text type="caption1">
        {formatInOriginalOffset(subscription.startTime, 'MM/dd/yyyy - hh:mm a')}
      </Text>
    ),
    'renewal-period': (
      <Text type="caption1">
        {getRenewalPeriodLabel(subscription.renewalPeriod)}
      </Text>
    ),
    'billing-cadence': (
      <Text type="caption1">
        {subscription.billingCadence
          ? capitalizeFirstLetter(subscription.billingCadence)
          : '-'}
      </Text>
    ),
    'car-details': (
      <PlateVinCell plate={subscription.car.plate} vin={subscription.car.vin} />
    ),
    'latest-trip-id': subscription.latestReservationId ? (
      <IdLink
        id={subscription.latestReservationId}
        href={`/partner/reservations/${subscription.latestReservationId}`}
      />
    ) : (
      <Text>-</Text>
    ),
    'outstanding-balance': (
      <Text type="bodyRegular">
        {formatCurrency(subscription.outstandingBalance)}
      </Text>
    ),
  }));

  return (
    <DataTable
      columns={columns}
      rows={rows}
      page={page}
      total={total}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      stickToBottom
    />
  );
};

const stylesheet = createStyleSheet((theme) => ({
  secondaryText: {
    color: theme.colors.text.secondary,
  },
}));
